<template>
  <div
    class="fts-automation-node-sequence automation-node"
    :class="{
      'automation-node--debug': data.debug,
      'automation-node--debugging': isDebugging && !isActionDebug,
    }"
  >
    <fts-automation-block-number :data="data" />

    <div class="node-header">
      <div class="node-header__label">
        <div class="node-header__label-icon node-header__label-icon--action">
          <i class="fts-icon-automation-action node-header-icon"></i>
        </div>

        <div class="node-header__label-text">
          Action
        </div>
      </div>

      <div class="node-header__action">
        <fts-automation-debugpoint
          v-model="data.debug"
          class="node-header__debugpoint"
          @input="updateData"
        />
      </div>
    </div>
    <!-- list -->
    <draggable
      handle=".node-sequence-handle"
      v-model="data.actions"
      class="node-sequence-list"
      v-if="data.actions && data.actions.length"
    >
      <div
        v-for="(action, index) in data.actions"
        :key="action.id"
        class="node-sequence-block"
        :class="{'node-sequence-border--debugging': isActionDebugging(action)}"
        @click="openAction(action)"
        @mouseover="selectedActionIndex = index"
        @mouseleave="selectedActionIndex = -1"
      >
        <font-awesome-icon
          :icon="['fas', 'grip-vertical']"
          class="node-sequence-handle stopPropagation stopDrag"
          style="user-select: none;"
        />

        <!-- exception -->
        <div 
          class="node-sequence-item-wrapper" 
          v-if="action.type == 'exception'"
        >
          <div class="node-sequence-item-icon">
            <i class="fts-icon-exclamation-triangle"></i>
          </div>

          <div class="node-sequence-item-content">
            <div class="node-sequence-item-header">
              <fts-automation-node-sequence-label 
                :module-id="action.moduleId" 
                :label-name="selectedAction(action).name" 
              />
              
              <div class="node-sequence-item-action">
                <font-awesome-icon
                  :icon="['far', 'trash-alt']"
                  class="node-sequence-item-trash stopPropagation"
                  @click="removeConfirmation(action)"
                  v-if="selectedActionIndex === index"
                />
                <fts-automation-debugpoint
                  v-model="action.debug"
                  class="stopPropagation"
                  v-if="selectedActionIndex === index || action.debug"
                />
              </div>
            </div>

            <fts-formula-display
              :formula="action.message"
              :variables="data.variables"
              v-if="action.message"
            />
          </div>
        </div>
        <!-- set variable -->
        <div 
          class="node-sequence-item-wrapper" 
          v-else-if="action.type === 'setVariable' || action.type === 'nextNumber'"
        >
          <div class="node-sequence-item-icon">
            <i class="fts-icon-plus"></i>
          </div>

          <div class="node-sequence-item-content">
            <div class="node-sequence-item-header">
              <fts-automation-node-sequence-label 
                :module-id="action.moduleId" 
                :label-name="selectedAction(action).name" 
              />

              <div class="node-sequence-item-action">
                <font-awesome-icon
                  :icon="['far', 'trash-alt']"
                  class="node-sequence-item-trash stopPropagation"
                  @click="removeConfirmation(action)"
                  v-if="selectedActionIndex === index"
                />
                <fts-automation-debugpoint
                  v-model="action.debug"
                  class="stopPropagation"
                  v-if="selectedActionIndex === index || action.debug"
                />
              </div>
            </div>

            <div class="node-sequence-item-tag" v-if="action.name">
              <div class="node-sequence-item-tag-wrapper">
                <fts-automation-label 
                  :label-name="action.name" 
                  :label-type="action.variableType"
                  :label-module-id="action.moduleId" 
                  class="node-sequence-item-tag-label" 
                />
              </div>
            </div>

            <fts-formula-display 
              :formula="action.value" 
              :variables="data.variables" 
              v-if="action.value" 
            />
          </div>
        </div>
        <!-- assign -->
        <div 
          class="node-sequence-item-wrapper" 
          v-else-if="action.type === 'assign'"
        >
          <div class="node-sequence-item-icon">
            <i class="fts-icon-edit"></i>
          </div>

          <div class="node-sequence-item-content">
            <div class="node-sequence-item-header">
              <fts-automation-node-sequence-label 
                :module-id="action.moduleId" 
                :label-name="selectedAction(action).name" 
              />

              <div class="node-sequence-item-action">
                <font-awesome-icon
                  :icon="['far', 'trash-alt']"
                  class="node-sequence-item-trash stopPropagation"
                  @click="removeConfirmation(action)"
                  v-if="selectedActionIndex === index"
                />
                <fts-automation-debugpoint
                  v-model="action.debug"
                  class="stopPropagation"
                  v-if="selectedActionIndex === index || action.debug"
                />
              </div>
            </div>

            <fts-formula-display
              :formula="action.variable"
              :variables="data.variables"
            />

            <fts-variable-assignment-display
              :moduleId="action.moduleId"
              :action="action"
              :variables="data.variables"
            />
          </div>
        </div>
        <!-- form -->
        <div 
          class="node-sequence-item-wrapper" 
          v-else-if="action.type === 'getForm'"
        >
          <div class="node-sequence-item-icon">
            <i class="fts-icon-form"></i>
          </div>

          <div class="node-sequence-item-content">
            <div class="node-sequence-item-header">
              <div class="node-sequence-item-header-wrapper">
                <fts-automation-node-sequence-label 
                  :module-id="action.moduleId" 
                  :activityId="action.activityId" 
                  :label-name="selectedAction(action).name" 
                />
              </div>

              <div class="node-sequence-item-action">
                <font-awesome-icon
                  :icon="['far', 'trash-alt']"
                  class="node-sequence-item-trash stopPropagation"
                  @click="removeConfirmation(action)"
                  v-if="selectedActionIndex === index"
                />
                <fts-automation-debugpoint
                  v-model="action.debug"
                  class="stopPropagation"
                  v-if="selectedActionIndex === index || action.debug"
                />
              </div>
            </div>

            <div class="node-sequence-item-tag" v-if="isVariable(action)">
              <fts-tippy class="node-sequence-item-tag-wrapper">
                <fts-automation-label 
                  :label-name="action.name" 
                  :label-type="action.variableType"
                  :label-module-id="action.moduleId" 
                  :is-list="action.isList" 
                  class="node-sequence-item-tag-label" 
                />
                <fts-automation-badge 
                  slot="content" 
                  :module-id="action.moduleId" 
                  :activity-id="action.activityId" 
                  :information-id="action.informationId" 
                />
              </fts-tippy>
            </div>
          </div>
        </div>
        <!-- find, find all -->
        <div
          class="node-sequence-item-wrapper" 
          v-else-if="action.type === 'find' || action.type === 'findAll'"
        >
          <div class="node-sequence-item-icon">
            <font-awesome-icon :icon="['fas', 'magnifying-glass']" flip="horizontal" />
          </div>

          <div class="node-sequence-item-content">
            <div class="node-sequence-item-header">
              <fts-automation-node-sequence-label 
                :module-id="action.moduleId" 
                :label-name="selectedAction(action).name" 
              />

              <div class="node-sequence-item-action">
                <font-awesome-icon
                  :icon="['far', 'trash-alt']"
                  class="node-sequence-item-trash stopPropagation"
                  @click="removeConfirmation(action)"
                  v-if="selectedActionIndex === index"
                />
                <fts-automation-debugpoint
                  v-model="action.debug"
                  class="stopPropagation"
                  v-if="selectedActionIndex === index || action.debug"
                />
              </div>
            </div>

            <div class="node-sequence-item-tag" v-if="isVariable(action)">
              <fts-tippy class="node-sequence-item-tag-wrapper">
                <fts-automation-label
                  :label-name="action.name"
                  :label-type="action.variableType"
                  :label-module-id="action.moduleId"
                  :is-list="action.isList"
                  class="node-sequence-item-tag-label"
                />
                <fts-automation-badge 
                  slot="content"
                  :module-id="action.moduleId"
                  :activity-id="action.activityId"
                  :information-id="action.informationId"
                />
              </fts-tippy>
            </div>

            <fts-automation-query-display 
              :module-id="action.moduleId" 
              :variables="data.variables" 
              :filter="action.filter"    
            />
          </div>
        </div>
        <!-- perform activity -->
        <div 
          class="node-sequence-item-wrapper"  
          v-else-if="action.type === 'doActivity'"
        >
          <div class="node-sequence-item-icon">
            <font-awesome-icon :icon="['fas', 'bolt']" />
          </div>

          <div class="node-sequence-item-content">
            <div class="node-sequence-item-header">
              <div class="node-sequence-item-header-wrapper">
                <fts-automation-node-sequence-label 
                  :module-id="action.moduleId" 
                  :activityId="action.activityId" 
                  :label-name="selectedAction(action).name" 
                />
              </div>

              <div class="node-sequence-item-action">
                <font-awesome-icon
                  :icon="['far', 'trash-alt']"
                  class="node-sequence-item-trash stopPropagation"
                  @click="removeConfirmation(action)"
                  v-if="selectedActionIndex === index"
                />
                <fts-automation-debugpoint
                  v-model="action.debug"
                  class="stopPropagation"
                  v-if="selectedActionIndex === index || action.debug"
                />
              </div>
            </div>

            <div class="node-sequence-item-tag" v-if="isVariable(action)">
              <fts-tippy class="node-sequence-item-tag-wrapper">
                <fts-automation-label 
                  :label-name="action.name" 
                  :label-type="action.variableType"
                  :label-module-id="action.moduleId" 
                  :is-list="action.isList" 
                  class="node-sequence-item-tag-label" 
                />

                <fts-automation-badge 
                  slot="content" 
                  :module-id="action.moduleId" 
                  :activity-id="action.activityId" 
                  :information-id="action.informationId" 
                />
              </fts-tippy>
            </div>

            <fts-formula-display
              :formula="action.entry"
              :variables="data.variables"
              v-if="action.entry"
            />
          </div>
        </div>
        <!-- plugin -->
        <div 
          class="node-sequence-item-wrapper" 
          v-else-if="action.type == 'plugin'"
        >
          <div class="node-sequence-item-icon">
            <i class="fts-icon-form"></i>
          </div>

          <div class="node-sequence-item-content">
            <div class="node-sequence-item-header">
              <fts-automation-node-sequence-label 
                :module-id="action.moduleId" 
                :label-name="getPluginName(action)" 
              />

              <div class="node-sequence-item-action">
                <font-awesome-icon
                  :icon="['far', 'trash-alt']"
                  class="node-sequence-item-trash stopPropagation"
                  @click="removeConfirmation(action)"
                  v-if="selectedActionIndex === index"
                />
                <fts-automation-debugpoint
                  v-model="action.debug"
                  class="stopPropagation"
                  v-if="selectedActionIndex === index || action.debug"
                />
              </div>
            </div>

            <div class="node-sequence-item-sub" v-if="action.name">
              {{action.name}}
            </div>
          </div>
        </div>
        <!-- logic service -->
        <div 
          class="node-sequence-item-wrapper" 
          v-else-if="action.type == 'logic'"
        >
          <div class="node-sequence-item-icon">
            <font-awesome-icon :icon="['fas', 'scroll']" />
          </div>

          <div class="node-sequence-item-content">
            <div class="node-sequence-item-header">
              <fts-automation-node-sequence-label 
                :module-id="action.moduleId" 
                :label-name="selectedAction(action).name" 
              />

              <div class="node-sequence-item-action">
                <font-awesome-icon
                  :icon="['far', 'trash-alt']"
                  class="node-sequence-item-trash stopPropagation"
                  @click="removeConfirmation(action)"
                  v-if="selectedActionIndex === index"
                />
                <fts-automation-debugpoint
                  v-model="action.debug"
                  class="stopPropagation"
                  v-if="selectedActionIndex === index || action.debug"
                />
              </div>
            </div>

            <div class="node-sequence-item-tag" v-if="isVariable(action)">
              <div class="node-sequence-item-tag-wrapper">
                <fts-automation-label
                  :label-name="action.name"
                  :label-type="action.variableType"
                  :label-module-id="action.moduleId"
                  :is-list="action.isList"
                  class="node-sequence-item-tag-label"
                />
              </div>
            </div>
          </div>
        </div>
        <!-- document -->
        <div 
          class="node-sequence-item-wrapper" 
          v-else-if="action.type == 'getDocument'"
        >
          <div class="node-sequence-item-icon">
            <i class="fts-icon-file"></i>
          </div>

          <div class="node-sequence-item-content">
            <div class="node-sequence-item-header">
              <fts-automation-node-sequence-label 
                :module-id="action.moduleId" 
                :label-name="selectedAction(action).name" 
              />

              <div class="node-sequence-item-action">
                <font-awesome-icon
                  :icon="['far', 'trash-alt']"
                  class="node-sequence-item-trash stopPropagation"
                  @click="removeConfirmation(action)"
                  v-if="selectedActionIndex === index"
                />
                <fts-automation-debugpoint
                  v-model="action.debug"
                  class="stopPropagation"
                  v-if="selectedActionIndex === index || action.debug"
                />
              </div>
            </div>

            <div class="node-sequence-item-tag" v-if="isVariable(action)">
              <fts-tippy class="node-sequence-item-tag-wrapper">
                <fts-automation-label 
                  :label-name="action.name" 
                  :label-type="action.variableType"
                  :label-module-id="action.moduleId" 
                  :is-list="action.isList" 
                  class="node-sequence-item-tag-label" 
                />
                <fts-automation-badge 
                  slot="content"
                  :module-id="action.moduleId" 
                  :activity-id="action.activityId" 
                  :information-id="action.informationId" 
                />
              </fts-tippy>
            </div>
          </div>
        </div>
        <!-- teammate (GNey) -->
        <div
          class="node-sequence-item-wrapper"
          v-else-if="action.type == 'teammate'"
        >
          <div class="node-sequence-item-icon">
            <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" />
          </div>

          <div class="node-sequence-item-content">
            <div class="node-sequence-item-header">
              <fts-automation-node-sequence-label
                :module-id="action.moduleId"
                :label-name="selectedAction(action).name"
              />

              <div class="node-sequence-item-action">
                <font-awesome-icon
                  :icon="['far', 'trash-alt']"
                  class="node-sequence-item-trash stopPropagation"
                  @click="removeConfirmation(action)"
                  v-if="selectedActionIndex === index"
                />
                <fts-automation-debugpoint
                  v-model="action.debug"
                  class="stopPropagation"
                  v-if="selectedActionIndex === index || action.debug"
                />
              </div>
            </div>

            <div class="node-sequence-item-tag" v-if="isVariable(action)">
              <div class="node-sequence-item-tag-wrapper">
                <fts-automation-label
                  :label-name="action.name"
                  :label-type="action.variableType"
                  :label-module-id="action.moduleId"
                  :is-list="action.isList"
                  class="node-sequence-item-tag-label"
                />
              </div>
            </div>
          </div>
        </div>
        <!-- others -->
        <div
          class="node-sequence-item-wrapper"
          v-else
        >
          <div class="node-sequence-item-icon">
            <i class="fts-icon-form"></i>
          </div>

          <div class="node-sequence-item-content">
            <div class="node-sequence-item-header">
              <fts-automation-node-sequence-label 
                :module-id="action.moduleId" 
                :label-name="selectedAction(action).name" 
              />

              <div class="node-sequence-item-action">
                <font-awesome-icon
                  :icon="['far', 'trash-alt']"
                  class="node-sequence-item-trash stopPropagation"
                  @click="removeConfirmation(action)"
                  v-if="selectedActionIndex === index"
                />
                <fts-automation-debugpoint
                  v-model="action.debug"
                  class="stopPropagation"
                  v-if="selectedActionIndex === index || action.debug"
                />
              </div>
            </div>

            <div v-if="isVariable(action)">
              <fts-automation-label
                :label-name="action.name"
                :label-type="action.variableType"
                  :label-module-id="action.moduleId"
                :is-list="action.isList"
                class="node-sequence-item-tag-label"
              />
              <fts-automation-badge
                :module-id="action.moduleId"
                :activity-id="action.activityId"
                :information-id="action.informationId"
                class="node-sequence-item-tag-badge"
              />
            </div>
          </div>
        </div>
      </div>
    </draggable>
    <!-- action -->
    <div class="node-sequence-action">
      <div class="node-sequence-action-btn" @click="openAction">
        <font-awesome-icon :icon="['fas', 'plus']" class="node-sequence-action-btn-icon" />Add
      </div>
    </div>
    <!-- automation action modal -->
    <fts-automation-action-modal ref="ftsAutomationActionModal" />
  </div>
</template>

<script>
import draggable from "vuedraggable";

import { automationDesignStore } from "../../store/automationDesignStore";
import commons from "../../commons/commons";
import automationBlocks from "../../commons/automationBlocks";

import FTSAutomationActionModal from "./FTSAutomationActionModal";
import FTSAutomationScopedVariables from "./FTSAutomationScopedVariables";
import FTSAutomationDebugpoint from "./FTSAutomationDebugpoint";
import FTSAutomationBlockNumber from "./FTSAutomationBlockNumber";
import FTSAutomationLabel from "./FTSAutomationLabel";
import FTSAutomationBadge from "./FTSAutomationBadge";
import FTSFormulaDisplay from "../FTSFormulaDisplay";
import FTSVariableAssignmentDisplay from "./FTSVariableAssignmentDisplay.vue";
import FTSAutomationNodeSequenceLabel from "./FTSAutomationNodeSequenceLabel";
import FTSAutomationQueryDisplay from "./FTSAutomationQueryDisplay";
import FTSMoji from "../../elements/FTSMoji";
import FTSTippy from "../../elements/FTSTippy";

export default {
  props: {
    data: Object
  },
  data: () => ({
    selectedActionIndex: -1,
    isDebugging: false,
    isActionDebug: false
  }),
  components: {
    draggable,
    "fts-automation-action-modal": FTSAutomationActionModal,
    "fts-automation-scoped-variables": FTSAutomationScopedVariables,
    "fts-automation-debugpoint": FTSAutomationDebugpoint,
    "fts-automation-block-number": FTSAutomationBlockNumber,
    "fts-automation-label": FTSAutomationLabel,
    "fts-automation-badge": FTSAutomationBadge,
    "fts-formula-display": FTSFormulaDisplay,
    "fts-variable-assignment-display": FTSVariableAssignmentDisplay,
    "fts-automation-node-sequence-label": FTSAutomationNodeSequenceLabel,
    "fts-automation-query-display": FTSAutomationQueryDisplay,
    "fts-moji": FTSMoji,
    "fts-tippy": FTSTippy
  },
  methods: {
    getPluginName(item) {
      let project = automationDesignStore.state.plugins.find(x => x.id == item.project);
      if(project) {
        let plugin = project.list.find(x => x.id == item.pluginType);
        if(plugin) {
          return project.name + ' ' + plugin.name;
        }
      }
    },
    updateData() {
      this.$forceUpdate();
    },
    isActionDebugging(action) {
      if (this.isDebugging) {
        return automationDesignStore.state.debugPayload.actionId == action.id;
      }
    },
    async openAction(item) {
      if (commons.elementStopPropagation()) return;
      let innerVariables = this.computeVariable(item);
      /* action */
      let action = { ...item };
      if (!action.id) {
        action.id = commons.nanoID();
      }
      /* actions */
      if (!Array.isArray(this.data.actions)) {
        this.data.actions = [];
      }
      let actions = this.data.actions;

      const result = await this.$refs.ftsAutomationActionModal.show(
        action,
        innerVariables,
        this.data
      );

      if (result != null && result != true) {
        const findIndex = actions.findIndex(x => x.id === result.id);
        if (findIndex > -1) this.$set(actions, findIndex, result);
        else actions.push(result);
        this.$forceUpdate();
        automationDesignStore.dispatch("updateVariables");
      } else if (result === true) {
        this.removeAction(action);
      }
    },
    computeVariable(item) {
      if (!this.data.actions) {
        return [...this.data.priorVariables];
      }

      let ind = this.data.actions.length;

      if (item && this.data.actions.indexOf(item) > -1) {
        ind = this.data.actions.indexOf(item);
      }

      let priors = this.data.actions
        .slice(0, ind)
        .filter(
          x =>
            (x.type == "plugin" && x.variable) ||
            (
              x.type != "plugin" &&
              automationBlocks.actionSelections.find(y => y.id == x.type)
            ).variable
        );

      return [...this.data.priorVariables, ...priors];
    },
    removeAction(item) {
      const findIndex = this.data.actions.findIndex(x => x.id === item.id);
      if (findIndex > -1) {
        this.data.actions.splice(findIndex, 1);
        this.$forceUpdate();
        automationDesignStore.dispatch("updateVariables");
      } else {
        this.$toasted.global.error();
      }
    },
    removeConfirmation(item) {
      let message = item.name;
      if (!message) {
        const name = this.selectedAction(item).name;
        message = `action ${name}`;
      }
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: `Delete ${message}?`,
        buttons: [
          { title: "Cancel" },
          {
            title: "Delete",
            default: true,
            handler: () => {
              this.removeAction(item);
              this.$modal.hide("dialog");
            }
          }
        ]
      });
    },
    isVariable(item) {
      if (item.type == "plugin") return item.variable;

      let action = automationBlocks.actionSelections.find(
        x => x.id == item.type
      );
      if (action) return action.variable;

      return false;
    },
    selectedAction(item) {
      if (item.type == "plugin") return item;

      return automationBlocks.actionSelections.find(x => x.id == item.type);
    },
    refreshBlockState() {
      this.isDebugging = false;
      this.isActionDebug = false;
      if (
        automationDesignStore.state.debugPayload &&
        automationDesignStore.state.debugPayload.nodeId
      ) {
        let nodeId = automationDesignStore.state.debugPayload.nodeId;
        let diagram = automationDesignStore.state.diagram;
        /* the debugged node may have been deleted since the payload was captured */
        if (diagram && diagram[nodeId] && diagram[nodeId].data.id == this.data.id) {
          this.isDebugging = true;
          this.isActionDebug = automationDesignStore.state.debugPayload.actionId;
        }
      }

      this.$forceUpdate();
    }
  },
  computed: {
    stateSource() {
      return this.$store.getters.workspaceStates;
    }
  },
  async mounted() {
    window.eventBus.$on("refreshAutomationBlocks", this.refreshBlockState);
  },
  beforeDestroy() {
    /* this handler only — a bare $off would strip every other block's listener too */
    window.eventBus.$off("refreshAutomationBlocks", this.refreshBlockState);
  }
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars.scss" as *;

.fts-automation-node-sequence {
  outline: 2px solid rgba(var(--fts-block-action-background-rgb), 0.25);
  .node-sequence-list {
    & + .node-sequence-action {
      padding-top: 0.25rem;
    }
    .node-sequence-block {
      position: relative;
      display: flex;
      align-items: flex-start;
      padding: 0.5rem 1rem;
      .node-sequence-handle {
        position: absolute;
        top: 0.875rem;
        left: 0.375rem;
        font-size: 0.625rem;
        color: var(--fts-default);
        cursor: grab;
        visibility: hidden;
      }
      &:not(:last-child) {
        border-bottom: 1px solid var(--fts-border);
      }
      &:hover {
        .node-sequence-handle {
          visibility: visible;
        }
      }
      &.node-sequence-border--debugging  {
        border-radius: 0.375rem;
        outline: 1px solid var(--fts-block-danger);
      }
    }
  }
  .node-sequence-action {
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 1rem;
    padding-bottom: 1rem;
    .node-sequence-action-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--fts-default);
      cursor: pointer;
      .node-sequence-action-btn-icon {
        margin-right: 0.375rem;
        font-size: 0.75rem;
      }
      &:hover {
        color: var(--fts-text);
      }
    }
  }
  .node-sequence-item-wrapper {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: flex-start;
  }
  .node-sequence-item-icon {
    position: relative;
    min-width: 1.313rem;
    min-height: 1.313rem;
    width: 1.313rem;
    height: 1.313rem;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 0.375rem;
  }
  .node-sequence-item-content {
    flex: 1;
    min-width: 0;
  }
  .node-sequence-item-header {
    display: flex;
    align-items: flex-start;
  }
  .node-sequence-item-header-wrapper {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
  }
  .node-sequence-item-action {
    height: 1.313rem;
    display: flex;
    align-items: center;
  }
  .node-sequence-item-trash {
    margin-right: 0.75rem;
    color: var(--fts-default);
    cursor: pointer;
    &:hover {
      color: var(--fts-danger);
    }
  }
  .node-sequence-item-sub {
    color: var(--fts-default);
  }
  .node-sequence-item-tag {
    margin-top: 0.125rem;
    &:not(:last-child) {
      margin-bottom: 0.125rem;
    }
  }
  .node-sequence-item-tag-wrapper {
    display: inline-flex;
    max-width: 100%;
    > .tippy-trigger {
      min-width: 0;
    }
  }
  .node-sequence-item-tag-label {
    padding-left: 0.375rem;
    padding-right: 0.375rem;
    border: 1px solid var(--fts-block-variable-border);
    border-radius: 0.25rem;
    color: var(--fts-default);
  }
  .fts-automation-node-sequence-label {
    flex: 1;
    min-width: 0;
  }
  .fts-automation-query-display {
    margin-top: 0.375rem;
  }
}
</style>